import { useEffect, useMemo, useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useRoleStore } from '@/lib/store';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Settings2 } from 'lucide-react';
import { useBranches } from '@/hooks/use-api';
import {
  ProfileTab,
  SecurityTab,
  PersonnelTab,
  BranchesTab,
  FinancialsTab,
  ComplianceTab,
  PwaTab,
  ErrorLogTab,
} from './settings';

type SettingsTab = 'profile' | 'security' | 'personnel' | 'branches' | 'financials' | 'compliance' | 'pwa' | 'errors';

export function SettingsPage() {
  const role = useRoleStore((s) => s.role);
  const storedBranchId = useRoleStore((s) => s.branchId);
  const { data: branches } = useBranches(role !== 'Client');

  const isAdmin = role === 'Admin';
  const canViewPersonnel = role === 'Admin' || role === 'Manager';
  const canViewBranches = isAdmin;
  const canViewFinancials = isAdmin || role === 'Accountant';
  const canViewCompliance = isAdmin;
  const canViewPwa = isAdmin;

  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  const displayBranchName = branches?.find((b) => b.id === storedBranchId)?.name || storedBranchId || 'Unassigned';

  const availableTabs = useMemo(
    () =>
      [
        { value: 'profile' as const, label: 'Profile', visible: true },
        { value: 'security' as const, label: 'Security', visible: true },
        { value: 'personnel' as const, label: 'Personnel', visible: canViewPersonnel },
        { value: 'branches' as const, label: 'Branches', visible: canViewBranches },
        { value: 'financials' as const, label: 'Financials', visible: canViewFinancials },
        { value: 'compliance' as const, label: 'Compliance', visible: canViewCompliance },
        { value: 'pwa' as const, label: 'App Icon', visible: canViewPwa },
        { value: 'errors' as const, label: 'Errors', visible: true },
      ].filter((tab) => tab.visible),
    [canViewBranches, canViewCompliance, canViewFinancials, canViewPersonnel, canViewPwa],
  );

  useEffect(() => {
    if (!availableTabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(availableTabs[0]?.value ?? 'profile');
    }
  }, [activeTab, availableTabs]);

  return (
    <AppLayout container className="bg-accent/20">
      <div className="mx-auto max-w-7xl space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 rounded-[2rem] border border-border bg-card/80 p-6 shadow-glass backdrop-blur md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
              <Settings2 className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-3xl font-black uppercase tracking-tight text-foreground">Settings</h1>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                Profile, security, and institutional controls.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className="border-none bg-secondary px-4 py-2 text-[10px] font-black uppercase tracking-widest">{role}</Badge>
            <Badge className="border-none bg-card px-4 py-2 text-[10px] font-black uppercase tracking-widest">{displayBranchName}</Badge>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as SettingsTab)} className="space-y-6">
          <TabsList className="flex flex-wrap gap-2 rounded-3xl border border-border bg-card p-2 shadow-glass">
            {availableTabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="rounded-2xl px-4 py-2.5 text-[11px] font-black uppercase tracking-tight data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="profile"><ProfileTab /></TabsContent>
          <TabsContent value="security"><SecurityTab /></TabsContent>
          <TabsContent value="personnel"><PersonnelTab /></TabsContent>
          <TabsContent value="branches"><BranchesTab /></TabsContent>
          <TabsContent value="financials"><FinancialsTab /></TabsContent>
          <TabsContent value="compliance"><ComplianceTab /></TabsContent>
          <TabsContent value="pwa"><PwaTab /></TabsContent>
          <TabsContent value="errors"><ErrorLogTab /></TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
